import { formatMXN } from '@/lib/api';
import { calcularIva, totalConIva } from '@/lib/precios';
import { cn } from '@/lib/utils';

interface PrecioDisplayProps {
  /** Precio unitario sin IVA */
  precio: number;
  precioLista?: number | null;
  cantidad?: number;
  variant?: 'card' | 'kit' | 'detail' | 'inline';
  etiquetaPrecio?: string;
  className?: string;
}

export function PrecioDisplay({
  precio,
  precioLista,
  cantidad = 1,
  variant = 'card',
  etiquetaPrecio,
  className,
}: PrecioDisplayProps) {
  const base = precio * cantidad;
  const iva = calcularIva(base);
  const total = totalConIva(base);
  const listaBase = precioLista != null ? precioLista * cantidad : null;
  const tieneOferta = listaBase != null && listaBase > base;

  if (variant === 'inline') {
    return (
      <span className={cn('tabular-nums', className)}>
        {formatMXN(total)} <span className="text-surface-500 font-normal">c/IVA</span>
      </span>
    );
  }

  if (variant === 'detail') {
    return (
      <div className={className}>
        <div className="flex flex-wrap items-end gap-3">
          {tieneOferta && (
            <span className="text-lg text-surface-400 line-through tabular-nums">
              {formatMXN(listaBase!)}
            </span>
          )}
          <span className="text-3xl font-bold tabular-nums text-brand-700">{formatMXN(base)}</span>
          <span className="text-sm text-surface-500">{etiquetaPrecio ?? 'sin IVA'}</span>
        </div>
        <div className="mt-2 space-y-0.5 text-sm text-surface-600">
          <p>
            IVA (16%): <span className="font-medium tabular-nums text-surface-800">{formatMXN(iva)}</span>
          </p>
          <p className="text-base font-semibold text-surface-900">
            Total con IVA:{' '}
            <span className="tabular-nums text-brand-700">{formatMXN(total)}</span>
          </p>
        </div>
      </div>
    );
  }

  const precioClass =
    variant === 'kit'
      ? 'text-xl font-bold tabular-nums text-brand-700'
      : 'font-mono text-base font-semibold tabular-nums text-surface-950';

  return (
    <div className={cn('min-w-0', className)}>
      {tieneOferta && (
        <p
          className={cn(
            'text-surface-400 line-through tabular-nums',
            variant === 'kit' ? 'text-sm' : 'font-mono text-[11px]',
          )}
        >
          {formatMXN(listaBase!)}
        </p>
      )}
      <p className={precioClass}>{formatMXN(base)}</p>
      <p className={cn('text-surface-500', variant === 'kit' ? 'text-xs' : 'font-mono text-[11px] leading-tight')}>
        + IVA {formatMXN(iva)}
      </p>
      <p
        className={cn(
          'font-medium tabular-nums text-brand-700',
          variant === 'kit' ? 'text-sm' : 'font-mono text-xs',
        )}
      >
        {formatMXN(total)} c/IVA
      </p>
      {etiquetaPrecio && variant === 'kit' && (
        <p className="mt-0.5 text-xs text-surface-500">{etiquetaPrecio}</p>
      )}
    </div>
  );
}
